import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import {
    AngularFirestore,
    AngularFirestoreDocument,
} from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { deleteField } from "firebase/firestore";
import * as lodash from 'lodash';
import moment from 'moment';
import { from, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, last, map, take } from 'rxjs/operators';
import {
    ADDRESSES_COLLECTION,
    BENEFITS_SERVICES_COLLECTION,
    COLLECTION_CONSULTANCY_PROCESS,
    COMPANIES_COLLECTION,
    CONFIRM_COP_WITHOUT_OPTIMIZATION_FUNCTION,
    COP_EMPLOYEE_FORM_STEP_INDEX,
    COP_SERVICES_SELECTION_STEP_INDEX,
    COP_SMARTPHONE_COLLECT_PERSON_LIST_STEP_INDEX,
    EMPLOYEE_ADVANTAGE_CALCULATE_FUNCTION,
    EMPLOYEES_COLLECTION,
    GENERATE_CONTRACT_PREVIEW,
    GET_EMPLOYEE_BENEFITS_SERVICES,
    IN_THE_CONSULTING_PROCESS,
    MAX_VALUE_INTERNET_COSTS,
    MAXIMUM_AGE_BAV,
    MAXIMUM_BAV_DEPOSIT,
    MINIMUM_BAV_PAYMENT,
    OP_EQUALS,
    PERSON_AGE_LIMIT_FOR_SMARTPHONE,
    SERVICE_HARDWARE,
    SERVICE_JOB_TICKET,
    SERVICE_MOBILE,
    SERVICE_MOBILE_CONTRACT,
    SERVICE_RETIREMENT,
    SERVICE_SALARY_INCREASE_SUMMARY,
    SERVICES_COLLECTION,
    STATUS_ACTIVE,
    STATUS_DISABLED, STATUS_DISABLED_BY_MOBILE_CONTRACT,
    STATUS_FULL,
    STATUS_INACTIVE, STATUS_OUT_OF_RANGE,
    UPDATE_EMPLOYEE_FORM_DATA,
    UPDATE_PERSON_SMARTPHONE_DATA,
    UPDATE_SERVICE_SELECTION_FUNCTION
} from '../../../app.constants';
import { ConsultancyProcess } from '../../../core/domain/employee-domain';
import { FunctionOptions } from '../../../core/domain/function-options';
import { BaseService } from '../../../core/services/base.service';
import { FireLoggingService } from '../../../core/services/fire-logging.service';
import { UserService } from '../../../core/services/user.service';
import { DateConverter } from '../../../core/utils/date-converter';
import { Product } from '../../../shared/models/product.model';


@Injectable({
    providedIn: 'root',
})
export class CopHomeService extends BaseService {
    // Emitters
    allowNextSectionsEmitter: EventEmitter<any> = new EventEmitter<any>();
    logoutEmitter: EventEmitter<any> = new EventEmitter<any>();
    goToSliderController: EventEmitter<any> = new EventEmitter<any>();
    prevAndNextSliderController: EventEmitter<any> = new EventEmitter<any>();
    prevAndNextSliderProperties: EventEmitter<any> = new EventEmitter<any>();
    copTransitionDoneEmitter: EventEmitter<any> = new EventEmitter<any>();
    selectionSliderEmitter: EventEmitter<any> = new EventEmitter<any>();

    // Variables
    hardwareService: any;
    consultancyProcessGlobal: ConsultancyProcess;
    previousConsultancyProcess: ConsultancyProcess;
    benefitsServices: any[] = [];
    copProgress: any = {
        services: {
            index: COP_SERVICES_SELECTION_STEP_INDEX,
            alreadyShowAlertModal: false,
        },
        employeeForm: {
            index: COP_EMPLOYEE_FORM_STEP_INDEX,
            alreadyShowAlertModal: false,
        },
        smartphoneForm: {
            index: COP_SMARTPHONE_COLLECT_PERSON_LIST_STEP_INDEX,
            alreadyShowAlertModal: false,
        },
    };
    updateConsultancyProcessWithDebounce = new Subject<{
        data: any;
        consultancyProcessId: string;
    }>();

    contractPreviewUrl: string;
    isCompanyHardwareServiceActive = false;
    isCompanyRetirementServiceActive = false;
    mustResubmitSmartphoneForm = false;
    mustResubmitEmployeeForm = false;

    constructor(
        protected override firestore: AngularFirestore,
        protected override http: HttpClient,
        protected override functions: AngularFireFunctions,
        protected userService: UserService,
        protected override fireLogging: FireLoggingService
    ) {
        super(firestore, http, functions, fireLogging);
        /**
         * Avoid multiple calls to firebase when the function
         * is called in the graph (selection-slider)
         */
        this.updateConsultancyProcessWithDebounce
            .pipe(
                last(),
                concatMap(({ data, consultancyProcessId }: { data: any; consultancyProcessId: string; }): any => {
                    return this.updateEmployeeConsultancyProcess(
                        data,
                        consultancyProcessId
                    );
                })
            )
            .subscribe();
    }

    getUserEmployee() {
        return this.firestore
            .collection(EMPLOYEES_COLLECTION)
            .doc(this.userService.loggedUser.id)
            .valueChanges()
            .pipe(
                map((entity: any) => {
                    return entity;
                }),
                catchError((error) => {
                    const message = error.error ? error.error.message : error.message;
                    this.fireLogging.sendErrorLog(`[getUserEmployee(cop-home.service.ts)] An error occurred
                    while getting item into collection ${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id},
                    details: ${message || JSON.stringify(error)}`);
                    return of({ error });
                })
            );
    }

    mustShowEmployeeForm() {
        const consultancyProcess = this.consultancyProcessGlobal;
        const employeeForm = consultancyProcess?.consultancyEmployeeForm;

        if (this.consultancyProcessGlobal?.returningConsultancyProcess) {
            const isEmployeeFormMissing = lodash.isNil(employeeForm);
            const isChildrenBelow25Missing = lodash.isNil(employeeForm?.childrenBelow25);
            const hasInternetAndItsLowerThanLimit = employeeForm?.monthlyCostsInternetContract != null && employeeForm?.monthlyCostsInternetContract < MAX_VALUE_INTERNET_COSTS;
            const shouldAskMonthlyCostsPublicTransport =
                this.isServiceActive(this.benefitsServices.find((service) => service.id === SERVICE_JOB_TICKET));
            const isForceResubmit = this.consultancyProcessGlobal.reasonToForceReSubmitForms && this.consultancyProcessGlobal.forceReSubmitForms;

            return isEmployeeFormMissing || isChildrenBelow25Missing || shouldAskMonthlyCostsPublicTransport || hasInternetAndItsLowerThanLimit || isForceResubmit;
        }

        return true;
    }

    mustShowSmartphoneForm() {
        return !this.consultancyProcessGlobal?.returningConsultancyProcess;
    }

    mustShowSelectionSlider() {
        return !this.isEmployeeUsingFullPotentialBAV() && !this.isEmployeeOlderThanTheBAVLimitAge() &&
            !this.isBenefitCalculatorAllChartsArraysEmpty();
    }

    mustShowServicesSelection() {
        const invalidStatuses = [
            STATUS_OUT_OF_RANGE,
            STATUS_DISABLED,
            STATUS_DISABLED_BY_MOBILE_CONTRACT,
            STATUS_FULL,
        ];
        const isServiceValid = (service: any): boolean => {
            const statusAllowed = !invalidStatuses.includes(service.status);
            const valueIsPositive = service.value > 0 || (service.valueLimit - service.tax) > 0;
            const isNotSalaryIncrease = service.id !== 'salary-increase';

            return statusAllowed && valueIsPositive && isNotSalaryIncrease;
        };
        return (this.benefitsServices?.filter(isServiceValid).length ?? 0) > 0; // Has at least one service valid
    }

    get allPeopleSmartphoneData() {
        const employeePersonData = {
            uid: this.userService.loggedUser.id,
            name: `${this.userService.loggedUser.firstName} ${this.userService.loggedUser.lastName}`,
            isEmployee: true,
            smartphoneData:
            this.consultancyProcessGlobal.employeeSmartphoneData,
        };

        if (
            this.consultancyProcessGlobal.consultancyEmployeeForm &&
            this.consultancyProcessGlobal.consultancyEmployeeForm
                .peopleAtSameHouse
        ) {
            const peopleAtSameHouseAndCompany =
                this.consultancyProcessGlobal.consultancyEmployeeForm
                    .peopleAtSameHouseAndCompany;
            const peopleAtSameHouse =
                this.consultancyProcessGlobal.consultancyEmployeeForm.peopleAtSameHouse.filter(
                    (person) => {
                        const dateObj = DateConverter.toDate(person.birthday);
                        let personWorksAtSameHouseAndCompany = false;
                        if (peopleAtSameHouseAndCompany) {
                            personWorksAtSameHouseAndCompany =
                                peopleAtSameHouseAndCompany.includes(
                                    person.uid
                                );
                        }
                        return (
                            [
                                'spouse',
                                'registered-life-partner',
                            ].includes(person.relationship) &&
                            moment().diff(dateObj, 'years') >=
                            PERSON_AGE_LIMIT_FOR_SMARTPHONE &&
                            !personWorksAtSameHouseAndCompany
                        );
                    }
                );
            if (peopleAtSameHouse) {
                return [ employeePersonData, ...peopleAtSameHouse ];
            }
        }
        return [ employeePersonData ];
    }

    generateContractPreview(): Observable<any> {
        return this.callFunction(
            this.userService.operatedByHelperUser ? {helperMetadata: {employeeId: this.userService.loggedUser.id, operatedByHelperUser: this.userService.operatedByHelperUser}} : null,
            GENERATE_CONTRACT_PREVIEW,
            new FunctionOptions('callable')
        );
    }

    getConsultancyProcess() {
        return this.firestore
            .collection(EMPLOYEES_COLLECTION)
            .doc(this.userService.loggedUser.id)
            .collection(COLLECTION_CONSULTANCY_PROCESS, (ref) =>
                ref.orderBy('createdAt', 'desc').limit(1))
            .snapshotChanges()
            .pipe(
                map((actions) => {
                    const doc = actions[0];
                    if (doc) {
                        return {
                            id: doc.payload.doc.id,
                            ...doc.payload.doc.data(),
                        };
                    }
                    return null;
                }),
                catchError((error) => {
                    const message = error.error ? error.error.message : error.message;
                    this.fireLogging.sendErrorLog(`[getConsultancyProcess(cop-home.service.ts)] An error
                    occurred while getting item into collection
                    ${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/${COLLECTION_CONSULTANCY_PROCESS},
                    details: ${message || JSON.stringify(error)}`);
                    return of({ error });
                })
            );
    }

    getConsultancyProcessOnce() {
        return this.firestore
            .collection(EMPLOYEES_COLLECTION)
            .doc(this.userService.loggedUser.id)
            .collection(COLLECTION_CONSULTANCY_PROCESS, (ref) =>
                ref.orderBy('createdAt', 'desc').limit(1))
            .get()
            .pipe(
                map((result) => {
                    if (result.empty) {
                        return null;
                    }
                    return {
                        id: result.docs[0].id,
                        ...result.docs[0].data(),
                    };
                }),
                catchError((error) => {
                    const message = error.error ? error.error.message : error.message;
                    this.fireLogging.sendErrorLog(`[getConsultancyProcessOnce(cop-home.service.ts)] An error
                    occurred while getting item into collection
                     ${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/${COLLECTION_CONSULTANCY_PROCESS},
                     details: ${message || JSON.stringify(error)}`);
                    return of({ error });
                })
            );
    }

    isReturningConsultancyProcess() {
        return (
            this.consultancyProcessGlobal &&
            this.consultancyProcessGlobal.returningConsultancyProcess
        );
    }

    isCopSalaryIncrease() {
        return (
            this.consultancyProcessGlobal &&
            this.consultancyProcessGlobal.isSalaryIncrease
        );
    }

    updateEmployeeFormData(entity: any): Observable<any> {
        return this.callFunction(
            {...entity, helperMetadata: {operatedByHelperUser: this.userService.operatedByHelperUser, employeeId: this.userService.loggedUser.id}},
            UPDATE_EMPLOYEE_FORM_DATA,
            new FunctionOptions('callable')
        );
    }

    updateRetirementService(): Observable<any> {
        const depositBAV =
            this.consultancyProcessGlobal?.netBenefitsDistribution?.depositBAV;
        const retirementServiceRef = this.firestore
            .collection(EMPLOYEES_COLLECTION)
            .doc(this.userService.loggedUser.id)
            .collection(COLLECTION_CONSULTANCY_PROCESS)
            .doc(this.consultancyProcessGlobal.id)
            .collection(BENEFITS_SERVICES_COLLECTION)
            .doc(SERVICE_RETIREMENT);

        const globalRetirementRef = this.firestore
            .collection(SERVICES_COLLECTION)
            .doc(SERVICE_RETIREMENT);

        if (depositBAV > 0) {
            if (this.consultancyProcessGlobal.returningConsultancyProcess) {
                return retirementServiceRef.get().pipe(
                    map(res => res.data()),
                    concatMap((retirementService: any) =>
                        this.setRetirementService(
                            globalRetirementRef,
                            retirementServiceRef,
                            depositBAV,
                            retirementService.previousValues,
                        )
                    )
                );
            } else {
                return this.setRetirementService(
                    globalRetirementRef,
                    retirementServiceRef,
                    depositBAV
                );
            }
        } else if (this.consultancyProcessGlobal.returningConsultancyProcess) {
            return retirementServiceRef.get().pipe(
                    map(res => res.data()),
                concatMap((retirementService: any) => {
                    if (retirementService.previousValues) {
                        return this.rollbackRetirementService(
                            globalRetirementRef,
                            retirementServiceRef,
                            retirementService.previousValues
                        );
                    }

                    return from(retirementServiceRef.delete());
                })
            );
        } else {
            return from(retirementServiceRef.delete());
        }
    }

    updateEmployeeStatusToConsultancyProcessStarted() {
        return from(
            this.firestore
                .collection(EMPLOYEES_COLLECTION)
                .doc(this.userService.loggedUser.id)
                .update({
                    status: IN_THE_CONSULTING_PROCESS,
                    lastModifiedFrom:
                        'WEBSITE (updateEmployeeStatusToConsultancyProcessStarted)',
                })
                .catch((error) => {
                    const message = error.error
                        ? error.error.message
                        : error.message;
                    this.fireLogging
                        .sendErrorLog(`[updateEmployeeStatusToConsultancyProcessStarted(cop-home.service.ts)]
                    An error occurred while updating item into collection
                    ${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id},
                    details: ${message || JSON.stringify(error)}`);
                })
        );
    }

    updateEmployeeConsultancyProcessWithDebounce(
        data: any,
        consultancyProcessId: string
    ): Observable<any> {
        this.updateConsultancyProcessWithDebounce.next({
            data,
            consultancyProcessId,
        });
        return this.updateConsultancyProcessWithDebounce.asObservable();
    }

    updateEmployeeConsultancyProcess(data: any, consultancyProcessId: string): any {
        if (this.userService.loggedUser?.id) {
            this.consultancyProcessGlobal = {
                ...this.consultancyProcessGlobal,
                ...data,
            };

            return from(
                this.firestore
                    .collection(EMPLOYEES_COLLECTION)
                    .doc(this.userService.loggedUser.id)
                    .collection(COLLECTION_CONSULTANCY_PROCESS)
                    .doc(consultancyProcessId)
                    .update({
                        ...data,
                        lastModifiedFrom:
                            'WEBSITE (updateEmployeeConsultancyProcess)',
                    })
                    .catch(error => {
                        const message = error.error ? error.error.message : error.message;
                        this.fireLogging.sendErrorLog(`[updateEmployeeConsultancyProcess(cop-home.service.ts)]
                        An error occurred while updating item into collection
                        ${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/${COLLECTION_CONSULTANCY_PROCESS}/
                        ${consultancyProcessId}, details: ${message || JSON.stringify(error)}`);
                    })
            );
        }
    }

    updateEmployeeBenefitService(
        data: any,
        serviceId: string
    ): Observable<any> {
        return new Observable((observer) => {
            this.firestore
                .collection(EMPLOYEES_COLLECTION)
                .doc(this.userService.loggedUser.id)
                .collection(COLLECTION_CONSULTANCY_PROCESS)
                .doc(this.consultancyProcessGlobal.id)
                .collection(BENEFITS_SERVICES_COLLECTION)
                .doc(serviceId)
                .update({
                    ...data,
                    lastModifiedFrom: 'WEBSITE (updateEmployeeBenefitService)',
                })
                .then(() => observer.next())
                .catch((error) => {
                    const message = error.error ? error.error.message : error.message;
                    const path = `${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/
                    ${COLLECTION_CONSULTANCY_PROCESS}/${this.consultancyProcessGlobal.id}/
                    ${BENEFITS_SERVICES_COLLECTION}/${serviceId}`;
                    this.fireLogging.sendErrorLog(`[updateEmployeeBenefitService(cop-home.service.ts)]
                    An error occurred while updating item into collection ${path},
                     details: ${message || JSON.stringify(error)}`);
                    observer.error();
                });
        });
    }

    markNetBenefitImportantInfoAsConfirmed() {
        return this.firestore
            .collection(EMPLOYEES_COLLECTION)
            .doc(this.userService.loggedUser.id)
            .collection(COLLECTION_CONSULTANCY_PROCESS)
            .doc(this.consultancyProcessGlobal.id)
            .update({
                netBenefitImportantInfoConfirmed: true,
                lastModifiedFrom:
                    'WEBSITE (markNetBenefitImportantInfoAsConfirmed)',
            })
            .catch(error => {
                const message = error.error ? error.error.message : error.message;
                this.fireLogging.sendErrorLog(`[markNetBenefitImportantInfoAsConfirmed(cop-home.service.ts)]
                An error occurred while updating item into collection
                ${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/${COLLECTION_CONSULTANCY_PROCESS}/
                ${this.consultancyProcessGlobal.id}, details: ${message || JSON.stringify(error)}`);
            });
    }

    markNetBenefitImportantInfoAfterExamplesAsConfirmed(confirmedAfterExamples: boolean) {
        return this.firestore
            .collection(EMPLOYEES_COLLECTION)
            .doc(this.userService.loggedUser.id)
            .collection(COLLECTION_CONSULTANCY_PROCESS)
            .doc(this.consultancyProcessGlobal.id)
            .update({
                netBenefitImportantInfoAfterExamplesConfirmed: confirmedAfterExamples,
                lastModifiedFrom:
                    'WEBSITE (markNetBenefitImportantInfoAfterExamplesAsConfirmed)',
            })
            .catch(error => {
                const message = error.error ? error.error.message : error.message;
                this.fireLogging.sendErrorLog(`[markNetBenefitImportantInfoAfterExamplesAsConfirmed
                (cop-home.service.ts)] An error occurred while updating item into collection
                ${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/${COLLECTION_CONSULTANCY_PROCESS}/
                ${this.consultancyProcessGlobal.id}, details: ${message || JSON.stringify(error)}`);
            });
    }

    markResultDetermineNetBenefitAsConfirmed() {
        return this.firestore
            .collection(EMPLOYEES_COLLECTION)
            .doc(this.userService.loggedUser.id)
            .collection(COLLECTION_CONSULTANCY_PROCESS)
            .doc(this.consultancyProcessGlobal.id)
            .update({
                resultDetermineNetBenefitConfirmed: true,
                lastModifiedFrom:
                    'WEBSITE (markResultDetermineNetBenefitAsConfirmed)',
            })
            .catch(error => {
                const message = error.error ? error.error.message : error.message;
                this.fireLogging.sendErrorLog(`[markResultDetermineNetBenefitAsConfirmed(cop-home.service.ts)]
                 An error occurred while updating item into collection
                 ${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/${COLLECTION_CONSULTANCY_PROCESS}/
                 ${this.consultancyProcessGlobal.id}, details: ${message || JSON.stringify(error)}`);
            });
    }

    noBenefitServiceWasSelected(benefitsServices?: any[]) {
        const services = benefitsServices || this.benefitsServices || [];
        const someServiceSelected = services.find((service) => {
            // The service salary increase is added in the benefits services list because it must have shown in the
            // services summary table.
            if (service.id !== 'salary-increase') {
                if (Array.isArray(service.instances)) {
                    return service.instances.find((instance: any) => [ STATUS_ACTIVE ].includes(instance.status));
                }
                return [ STATUS_ACTIVE ].includes(service.status);
            }
        });
        return !!!someServiceSelected;
    }

    hasInactiveService(benefitsServices?: any[]) {
        return benefitsServices?.some((service) => service.status === STATUS_INACTIVE);
    }

    onlyHasDataAtChartExtended() {
        const chart = this.getChart();
        const chartExtended = this.getChartExtended();
        return chart.length === 0 && chartExtended.length > 0;
    }

    noBenefitServiceWasSelectedOrOnlyHasDataAtChartExtended(
        benefitsServices?: any[]
    ) {
        const chart = this.getChart();
        const chartExtended = this.getChartExtended();
        return (
            this.noBenefitServiceWasSelected(benefitsServices) ||
            (chart.length === 0 && chartExtended.length > 0)
        );
    }

    noBenefitServiceWasSelectedAndOnlyHasDataAtChartExtended(
        benefitsServices?: any[]
    ) {
        const chart = this.getChart();
        const chartExtended = this.getChartExtended();
        return (
            this.noBenefitServiceWasSelected(benefitsServices) &&
            (chart.length === 0 && chartExtended.length > 0)
        );
    }

    isDepositBavEqualsZero() {
        if (this.consultancyProcessGlobal) {
            return (
                !this.consultancyProcessGlobal.netBenefitsDistribution ||
                this.consultancyProcessGlobal.netBenefitsDistribution
                    .depositBAV === 0
            );
        }
        return false;
    }

    updatePersonSmartphoneData(entity: any): Observable<any> {
        return this.callFunction(
            {...entity, helperMetadata: {employeeId: this.userService.loggedUser.id, operatedByHelperUser: this.userService.operatedByHelperUser}},
            UPDATE_PERSON_SMARTPHONE_DATA,
            new FunctionOptions('callable')
        );
    }

    getAvailableValuesOfAdvantageCalculator(
        benefitCalculatorInput: any
    ): Observable<any> {
        return this.callFunction(
            benefitCalculatorInput,
            EMPLOYEE_ADVANTAGE_CALCULATE_FUNCTION,
            new FunctionOptions('callable')
        );
    }

    getBenefitsServicesByEmployeeId(): Observable<any> {
        return this.callFunction(
            this.userService.operatedByHelperUser ? {helperMetadata: {employeeId: this.userService.loggedUser.id, operatedByHelperUser: true}} : null,
            GET_EMPLOYEE_BENEFITS_SERVICES,
            new FunctionOptions('callable')
        );
    }

    updateMobileContractData(
        personId: string,
        contractPhoneNumber: string,
        contractHolder: string
    ): Observable<any> {
        return new Observable((observer) => {
            this.firestore
                .collection(EMPLOYEES_COLLECTION)
                .doc(this.userService.loggedUser.id)
                .collection(COLLECTION_CONSULTANCY_PROCESS)
                .doc(this.consultancyProcessGlobal.id)
                .collection(BENEFITS_SERVICES_COLLECTION)
                .doc(SERVICE_MOBILE_CONTRACT)
                .get()
                .subscribe(
                    (doc) => {
                        const service: any = doc.data();
                        if (service && service.instances) {
                            const instanceIndex = service.instances.findIndex(
                                (item: any) => item.personId === personId
                            );
                            if (instanceIndex !== -1) {
                                service.instances[
                                    instanceIndex
                                    ].contractPhoneNumber = contractPhoneNumber;
                                service.instances[
                                    instanceIndex
                                    ].contractHolder = contractHolder;
                                this.firestore
                                    .collection(EMPLOYEES_COLLECTION)
                                    .doc(this.userService.loggedUser.id)
                                    .collection(COLLECTION_CONSULTANCY_PROCESS)
                                    .doc(this.consultancyProcessGlobal.id)
                                    .collection(BENEFITS_SERVICES_COLLECTION)
                                    .doc(SERVICE_MOBILE_CONTRACT)
                                    .update({
                                        instances: service.instances,
                                        lastModifiedFrom:
                                            'WEBSITE (updateMobileContractData)',
                                    })
                                    .then(() => observer.next())
                                    .catch((error) => {
                                        const path = `${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/
                                        ${COLLECTION_CONSULTANCY_PROCESS}/${this.consultancyProcessGlobal.id}/
                                        ${BENEFITS_SERVICES_COLLECTION}/${SERVICE_MOBILE_CONTRACT}`;
                                        const message = error.error ? error.error.message : error.message;
                                        this.fireLogging.sendErrorLog(`[updateMobileContractData(user.service.ts)]
                                         An error occurred while updating item into collection ${path},
                                         details: ${message || JSON.stringify(error)}`);
                                        observer.error(error);
                                    });
                            }
                        } else {
                            observer.error();
                        }
                    },
                    (error) => {
                        const path = `${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/
                        ${COLLECTION_CONSULTANCY_PROCESS}/${this.consultancyProcessGlobal.id}/
                        ${BENEFITS_SERVICES_COLLECTION}/${SERVICE_MOBILE_CONTRACT}`;
                        const message = error.error ? error.error.message : error.message;
                        this.fireLogging.sendErrorLog(`[updateMobileContractData(user.service.ts)] An error
                        occurred while getting item into collection ${path},
                        details: ${message || JSON.stringify(error)}`);
                        observer.error(error);
                    }
                );
        });
    }

    updateServiceSelection(
        serviceId: string,
        status: any,
        personId?: string | null,
        customValue?: number | null,
        forceCalculate?: boolean
    ): Observable<any> {
        const data = {
            personId,
            serviceId,
            status,
            customValue,
            forceCalculate,
        };
        return this.callFunction(
            {...data, helperMetadata: {employeeId: this.userService.loggedUser.id, operatedByHelperUser: this.userService.operatedByHelperUser}},
            UPDATE_SERVICE_SELECTION_FUNCTION,
            new FunctionOptions('callable')
        );
    }

    checkIfCompanyHardwareServiceActive() {
        return this.firestore
            .collection(COMPANIES_COLLECTION)
            .doc(this.userService.loggedUser.companyId)
            .collection(BENEFITS_SERVICES_COLLECTION)
            .doc(SERVICE_HARDWARE)
            .get()
            .pipe(map(item => {
                const data: any = item.data();
                const result = item.exists && (data?.status !== STATUS_DISABLED && data?.status !== STATUS_INACTIVE);
                this.isCompanyHardwareServiceActive = result;
                return result;
            }));
    }

    checkIfCompanyRetirementServiceActive() {
        return this.firestore
            .collection(COMPANIES_COLLECTION)
            .doc(this.userService.loggedUser.companyId)
            .collection(BENEFITS_SERVICES_COLLECTION)
            .doc(SERVICE_RETIREMENT)
            .get()
            .pipe(map(item => {
                const data: any = item.data();
                const result = item.exists && (data?.status !== STATUS_DISABLED && data?.status !== STATUS_INACTIVE);
                this.isCompanyRetirementServiceActive = result;
                return result;
            }));
    }

    mustShowStoreCopV2() {
        if (this.consultancyProcessGlobal.returningConsultancyProcess) {
            const hardware = this.hardwareService;
            if (hardware) {
                const peopleAtSameHouse = this.consultancyProcessGlobal?.consultancyEmployeeForm?.peopleAtSameHouse || [];
                const peopleAtSameThatCanGetSmartphone = peopleAtSameHouse.filter(p => p.smartphoneData?.output?.memberCanGetNewMobile);
                const allPeopleThatCanGetSmartphone = [ ...peopleAtSameThatCanGetSmartphone ];
                const hasSomeoneWithSampleProduct = !!hardware.instances?.find((inst: Product) => inst.isASimpleProduct);
                if (hasSomeoneWithSampleProduct) {
                    return true;
                } else {
                    if (this.consultancyProcessGlobal?.employeeSmartphoneData?.output?.employeeCanGetNewMobile ||
                        this.consultancyProcessGlobal?.employeeSmartphoneData?.output?.employeeMustGetNewMobile) {
                        allPeopleThatCanGetSmartphone.push(this.userService.loggedUser);
                    }
                    const instancesFull = hardware.instances?.filter((inst: any) => inst.status === STATUS_FULL);
                    if (allPeopleThatCanGetSmartphone.length === instancesFull?.length) {
                        return false;
                    }
                }
            }
        }
        return true;
    }

    async markPopoverAlertAsViewed(serviceId: string, params?: any) {
        let data: any = {};
        if ([ SERVICE_MOBILE, SERVICE_MOBILE_CONTRACT ].includes(serviceId)) {
            const serviceMobile: any = await this.firestore
                .collection(EMPLOYEES_COLLECTION)
                .doc(this.userService.loggedUser.id)
                .collection(COLLECTION_CONSULTANCY_PROCESS)
                .doc(this.consultancyProcessGlobal.id)
                .collection(BENEFITS_SERVICES_COLLECTION)
                .doc(serviceId)
                .get(data)
                .toPromise()
                .then((res) => res!.data())
                .catch(error => {
                    const path = `${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/
                    ${COLLECTION_CONSULTANCY_PROCESS}/${this.consultancyProcessGlobal.id}/
                    ${BENEFITS_SERVICES_COLLECTION}/${serviceId}`;
                    const message = error.error ? error.error.message : error.message;
                    this.fireLogging.sendErrorLog(`[markPopoverAlertAsViewed(user.service.ts)] An error occurred
                    while updating item into collection ${path}, details: ${message || JSON.stringify(error)}`);
                });
            serviceMobile.instances.forEach((instance: any) => {
                if (params && instance.personId === params.personId) {
                    instance.popoverAlertViewed = true;
                }
            });
            delete serviceMobile.service;
            data = { ...serviceMobile };
        } else {
            data.popoverAlertViewed = true;
        }
        return this.firestore
            .collection(EMPLOYEES_COLLECTION)
            .doc(this.userService.loggedUser.id)
            .collection(COLLECTION_CONSULTANCY_PROCESS)
            .doc(this.consultancyProcessGlobal.id)
            .collection(BENEFITS_SERVICES_COLLECTION)
            .doc(serviceId)
            .update({
                ...data,
                lastModifiedFrom: 'WEBSITE (markPopoverAlertAsViewed)',
            })
            .catch(error => {
                const path = `${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/
                ${COLLECTION_CONSULTANCY_PROCESS}/${this.consultancyProcessGlobal.id}/
                ${BENEFITS_SERVICES_COLLECTION}/${serviceId}`;
                const message = error.error ? error.error.message : error.message;
                this.fireLogging.sendErrorLog(`[markPopoverAlertAsViewed(user.service.ts)] An error occurred while
                 updating item into collection ${path}, details: ${message || JSON.stringify(error)}`);
            });
    }

    agreeWithSummaryServicesValues(value: boolean) {
        return this.firestore
            .collection(EMPLOYEES_COLLECTION)
            .doc(this.userService.loggedUser.id)
            .collection(COLLECTION_CONSULTANCY_PROCESS)
            .doc(this.consultancyProcessGlobal.id)
            .update({
                agreeWithSummaryServicesValues: value,
                lastModifiedFrom: 'WEBSITE (agreeWithSummaryServicesValues)',
            })
            .catch(error => {
                const path = `${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/
                ${COLLECTION_CONSULTANCY_PROCESS}/${this.consultancyProcessGlobal.id}`;
                const message = error.error ? error.error.message : error.message;
                this.fireLogging.sendErrorLog(`[agreeWithSummaryServicesValues(user.service.ts)] An error
                occurred while updating item into collection ${path}, details: ${message || JSON.stringify(error)}`);
            });
    }

    updateCopCurrentStep(currentStep: number) {
        return this.firestore
            .collection(EMPLOYEES_COLLECTION)
            .doc(this.userService.loggedUser.id)
            .collection(COLLECTION_CONSULTANCY_PROCESS)
            .doc(this.consultancyProcessGlobal.id)
            .update({
                currentStep: currentStep,
                lastModifiedFrom: 'WEBSITE (updateCopCurrentStep)',
            })
            .catch(error => {
                const path = `${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/
                ${COLLECTION_CONSULTANCY_PROCESS}/${this.consultancyProcessGlobal.id}`;
                const message = error.error ? error.error.message : error.message;
                this.fireLogging.sendErrorLog(`[updateCopCurrentStep(user.service.ts)] An error occurred while
                 updating item into collection ${path}, details: ${message || JSON.stringify(error)}`);
            });
    }

    getCompanyUnitsByCompanyId(companyId: string): Observable<any> {
        return this.firestore
            .collection(COMPANIES_COLLECTION)
            .doc(companyId)
            .collection(ADDRESSES_COLLECTION, (ref) => {
                return ref
                    .where('status', OP_EQUALS, STATUS_ACTIVE)
                    .where('type', OP_EQUALS, 'WORK ADDRESS');
            })
            .snapshotChanges()
            .pipe(
                map((actions) =>
                    actions.map((action) => {
                        const data: any = action.payload.doc.data();
                        data.id = action.payload.doc.id;
                        return data;
                    })
                ),
                catchError((error) => {
                    const message = error.error ? error.error.message : error.message;
                    this.fireLogging.sendErrorLog(`[getCompanyUnitsByCompanyId(user.service.ts)] An error
                    occurred while getting item into collection ${COMPANIES_COLLECTION}/${companyId}/
                    ${ADDRESSES_COLLECTION}, details: ${message || JSON.stringify(error)}`);
                    return of({ error });
                })
            );
    }

    getEmployeeAddress(employeeId: string) {
        return this.firestore
            .collection(EMPLOYEES_COLLECTION)
            .doc(employeeId)
            .collection(ADDRESSES_COLLECTION, (ref) =>
                ref.where('status', OP_EQUALS, STATUS_ACTIVE).limit(1)
            )
            .snapshotChanges()
            .pipe(
                map((actions) => {
                    const doc = actions[0];
                    return {
                        id: doc.payload.doc.id,
                        ...doc.payload.doc.data(),
                    };
                }),
                catchError((error) => {
                    const message = error.error ? error.error.message : error.message;
                    this.fireLogging.sendErrorLog(`[getEmployeeAddress(user.service.ts)] An error occurred
                    while getting item into collection ${EMPLOYEES_COLLECTION}/${employeeId}/${ADDRESSES_COLLECTION},
                    details: ${message || JSON.stringify(error)}`);
                    return of({ error });
                })
            );
    }

    async markServiceAsViewed(serviceId: string, params?: any) {
        let data: any = {};
        if ([ SERVICE_MOBILE, SERVICE_MOBILE_CONTRACT ].includes(serviceId)) {
            const serviceMobile: any = await this.firestore
                .collection(EMPLOYEES_COLLECTION)
                .doc(this.userService.loggedUser.id)
                .collection(COLLECTION_CONSULTANCY_PROCESS)
                .doc(this.consultancyProcessGlobal.id)
                .collection(BENEFITS_SERVICES_COLLECTION)
                .doc(serviceId)
                .get(data)
                .toPromise()
                .then((res: any) => res.data())
                .catch(error => {
                    const path = `${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/
                    ${COLLECTION_CONSULTANCY_PROCESS}/${this.consultancyProcessGlobal.id}/
                    ${BENEFITS_SERVICES_COLLECTION}/${serviceId}`;
                    const message = error.error ? error.error.message : error.message;
                    this.fireLogging.sendErrorLog(`[markServiceAsViewed(user.service.ts)] An error occurred
                    while updating item into collection ${path}, details: ${message || JSON.stringify(error)}`);
                });
            serviceMobile.instances.forEach((instance: any) => {
                if (params && instance.personId === params.personId) {
                    instance.viewed = true;
                }
            });
            delete serviceMobile.service;
            data = { ...serviceMobile };
        } else {
            data.viewed = true;
        }
        return this.firestore
            .collection(EMPLOYEES_COLLECTION)
            .doc(this.userService.loggedUser.id)
            .collection(COLLECTION_CONSULTANCY_PROCESS)
            .doc(this.consultancyProcessGlobal.id)
            .collection(BENEFITS_SERVICES_COLLECTION)
            .doc(serviceId)
            .update({
                ...data,
                lastModifiedFrom: 'WEBSITE (markServiceAsViewed)',
            })
            .catch(error => {
                const path = `${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/
                ${COLLECTION_CONSULTANCY_PROCESS}/${this.consultancyProcessGlobal.id}/
                ${BENEFITS_SERVICES_COLLECTION}/${serviceId}`;
                const message = error.error ? error.error.message : error.message;
                this.fireLogging.sendErrorLog(`[markServiceAsViewed(user.service.ts)] An error occurred while
                 updating item into collection ${path}, details: ${message || JSON.stringify(error)}`);
            });
    }

    updateCopEmployeeFormLocally(employeeForm: any) {
        this.consultancyProcessGlobal.consultancyEmployeeForm = {
            ...this.consultancyProcessGlobal.consultancyEmployeeForm,
            ...employeeForm,
        };
    }

    updateCopEmployeeSmartphoneFormLocally(smartphoneForm: any) {
        // Update the employee
        if (smartphoneForm.isEmployee) {
            const employeeInput =
                smartphoneForm.basicPersonalData.smartphoneData.input;
            if (!this.consultancyProcessGlobal.employeeSmartphoneData) {
                this.consultancyProcessGlobal.employeeSmartphoneData = {
                    input: {},
                    output: {},
                };
            }
            this.consultancyProcessGlobal.employeeSmartphoneData.input =
                employeeInput;
        } else {
            // Update the people at the same house
            this.consultancyProcessGlobal.consultancyEmployeeForm!.peopleAtSameHouse.map(
                (item) => {
                    if (
                        item.name === smartphoneForm.basicPersonalData.name &&
                        item.birthday ===
                        smartphoneForm.basicPersonalData.birthday
                    ) {
                        const input =
                            smartphoneForm.basicPersonalData.smartphoneData
                                .input;
                        if (!item.smartphoneData) {
                            item.smartphoneData = { input: {}, output: {} };
                        }
                        item.smartphoneData.input = input;
                    }
                }
            );
        }
    }

    getListOfPersonsPossibleToGetSmartphone() {
        const smartphoneData =
            this.consultancyProcessGlobal.employeeSmartphoneData;

        const employeeData = {
            isEmployee: true,
            uid: this.userService.loggedUser.id,
            firstName: this.userService.loggedUser.firstName,
            lastName: this.userService.loggedUser.lastName,
            employeeCanGetNewMobile: smartphoneData.output
                .employeeCanGetNewMobile
                ? smartphoneData.output.employeeCanGetNewMobile
                : false,
            employeeMustGetNewMobile: smartphoneData.output
                .employeeMustGetNewMobile
                ? smartphoneData.output.employeeMustGetNewMobile
                : false,
            employerProvidesYouACellPhone: smartphoneData.output
                .employerProvidesYouACellPhone
                ? smartphoneData.output.employerProvidesYouACellPhone
                : false,
            didYouReceivedYourMobileFromEmployerMoreThan20MonthsAgo:
                !!smartphoneData.input.didYouReceivedYourMobileFromEmployerMoreThan20MonthsAgo,
            doesTheCellPhoneMeetTechnicalRequirements:
                !!smartphoneData.input.doesTheCellPhoneMeetTechnicalRequirements,
            isTheContractCostPaidByEmployer:
                !!smartphoneData.input.isTheContractCostPaidByEmployer
        };

        const personMembers =
            (this.consultancyProcessGlobal.consultancyEmployeeForm &&
                this.consultancyProcessGlobal.consultancyEmployeeForm.peopleAtSameHouse
                    .filter(
                        (item) =>
                            item.smartphoneData && item.smartphoneData.output
                    )
                    .map((item) => {
                        return {
                            isEmployee: false,
                            firstName: item.firstName,
                            lastName: item.lastName,
                            uid: item.uid,
                            memberCanGetNewMobile: item.smartphoneData.output
                                .memberCanGetNewMobile
                                ? item.smartphoneData.output
                                    .memberCanGetNewMobile
                                : false,
                            employerProvidesYouACellPhone: item.smartphoneData
                                .output.employerProvidesYouACellPhone
                                ? item.smartphoneData.output
                                    .employerProvidesYouACellPhone
                                : false,
                            didYouReceivedYourMobileFromEmployerMoreThan20MonthsAgo:
                                !!item.smartphoneData.input.didYouReceivedYourMobileFromEmployerMoreThan20MonthsAgo,
                            isTheContractCostPaidByEmployer:
                                !!item.smartphoneData.input.isTheContractCostPaidByEmployer
                        };
                    })) ||
            [];

        return [ employeeData, ...personMembers ];
    }

    getListOfPersonWillReceiveSimpleSmartphone() {
        const possibleToGetSmartphone = this.getListOfPersonsPossibleToGetSmartphone();
        const willReceiveSimpleSmartphone = possibleToGetSmartphone.filter((person: any) => {
            if (person.isEmployee) {
                if (person.employeeCanGetNewMobile || person.employeeMustGetNewMobile) {
                    if (this.isAnyMobileServiceActive(person) && !person.employerProvidesYouACellPhone) {
                        return person;
                    }
                }
            } else {
                if (person.memberCanGetNewMobile && this.isAnyMobileServiceActive(person) &&
                    !person.employerProvidesYouACellPhone) {
                    return person;
                }
            }
        });

        return willReceiveSimpleSmartphone;
    }

    isAnyMobileServiceActive(person: any) {
        let hasAnyMobileActive = false;
        if (this.benefitsServices.length) {
            hasAnyMobileActive = this.benefitsServices.some((service) =>
                (service.id === SERVICE_MOBILE || service.id === SERVICE_MOBILE_CONTRACT) &&
                service.instances.some((instance: any) => {
                    if (person) {
                        return (person.uid === instance.personId) && instance.status === STATUS_ACTIVE;
                    } else {
                        return instance.isEmployee && instance.status === STATUS_ACTIVE;
                    }
                }));
        }
        return hasAnyMobileActive;
    }

    checkIfAnyMobileServiceIsActive() {
        let hasAnyMobileActive = false;
        if (this.benefitsServices.length) {
            hasAnyMobileActive = this.benefitsServices.some((service) =>
                (service.id === SERVICE_MOBILE || service.id === SERVICE_MOBILE_CONTRACT) &&
                service.instances.some((instance: any) => instance.status === STATUS_ACTIVE));
        }
        return hasAnyMobileActive;
    }

    isServiceActive(service: any) {
        return service?.status === STATUS_ACTIVE ||
            service?.previousValues?.status === STATUS_ACTIVE ||
            this.isFullCase(service?.status, service?.previousValues?.status);
    }

    isFullCase(currentStatus: string, previousStatus: string): boolean {
        const validCurrentStatuses = [
            STATUS_FULL,
            STATUS_OUT_OF_RANGE,
            STATUS_DISABLED,
            STATUS_DISABLED_BY_MOBILE_CONTRACT,
            STATUS_INACTIVE,
        ];

        const validPreviousStatuses = [
            STATUS_ACTIVE,
            STATUS_FULL,
        ];

        return validCurrentStatuses.includes(currentStatus) && validPreviousStatuses.includes(previousStatus);
    }

    increaseEmployeeIsUnableToChooseSmartphone() {
        const employeeForm: any = this.consultancyProcessGlobal.consultancyEmployeeForm;
        const familyMembersAbleToChoose = employeeForm.peopleAtSameHouse
            .filter((person: any) => person.smartphoneData &&
                person.smartphoneData.input && person.smartphoneData.output
            );
        return this.consultancyProcessGlobal.isSalaryIncrease &&
            this.getListOfPersonWillReceiveSimpleSmartphone().length === familyMembersAbleToChoose.length + 1;
    }

    mustShowDialogWarningAboutSmartphoneForm() {
        const serviceKey: any = Object.keys(this.copProgress).find(
            (item: any) =>
                this.copProgress[item].index ===
                COP_SMARTPHONE_COLLECT_PERSON_LIST_STEP_INDEX
        );
        if (serviceKey) {
            return (
                this.consultancyProcessGlobal
                    .distributeNetBenefitWelcomeVideoWatched &&
                !this.copProgress[serviceKey].alreadyShowAlertModal
            );
        }
        return false;
    }

    mustShowDialogWarningAboutEmployeeForm() {
        const serviceKey: any = Object.keys(this.copProgress).find(
            (item: any) =>
                this.copProgress[item].index === COP_EMPLOYEE_FORM_STEP_INDEX
        );
        if (serviceKey) {
            return (
                this.consultancyProcessGlobal
                    .distributeNetBenefitWelcomeVideoWatched &&
                !this.copProgress[serviceKey].alreadyShowAlertModal
            );
        }
        return false;
    }

    mustShowDialogWarningAboutServiceChanges() {
        const serviceKey: any = Object.keys(this.copProgress).find(
            (item: any) =>
                this.copProgress[item].index ===
                COP_SERVICES_SELECTION_STEP_INDEX
        );
        if (serviceKey) {
            return (
                this.consultancyProcessGlobal
                    .distributeNetBenefitWelcomeVideoWatched &&
                !this.copProgress[serviceKey].alreadyShowAlertModal
            );
        }
        return false;
    }

    updateBenefitsServicesSelectionViewed(value: boolean) {
        this.firestore
            .collection(EMPLOYEES_COLLECTION)
            .doc(this.userService.loggedUser.id)
            .collection(COLLECTION_CONSULTANCY_PROCESS)
            .doc(this.consultancyProcessGlobal.id)
            .update({
                selectBenefitServicesViewed: !!value,
                lastModifiedFrom:
                    'WEBSITE (updateBenefitsServicesSelectionViewed)',
            })
            .catch(error => {
                const path = `${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/
                ${COLLECTION_CONSULTANCY_PROCESS}/${this.consultancyProcessGlobal.id}`;
                const message = error.error ? error.error.message : error.message;
                this.fireLogging.sendErrorLog(`[updateBenefitsServicesSelectionViewed(user.service.ts)]
                An error occurred while updating item into collection ${path},
                details: ${message || JSON.stringify(error)}`);
            });
        this.consultancyProcessGlobal.selectBenefitServicesViewed = !!value;
    }

    markDialogWarningChangesAsViewed(indexStep: number, confirmed: boolean) {
        const serviceKey: any = Object.keys(this.copProgress).find(
            (item: any) => this.copProgress[item].index === indexStep
        );
        if (serviceKey) {
            this.copProgress[serviceKey] = {
                ...this.copProgress[serviceKey],
                alreadyShowAlertModal: confirmed,
            };
        }
    }

    loadHardwareService() {
        return this.firestore.collection(EMPLOYEES_COLLECTION)
            .doc(this.userService.loggedUser.id)
            .collection(COLLECTION_CONSULTANCY_PROCESS)
            .doc(this.consultancyProcessGlobal?.id)
            .collection(BENEFITS_SERVICES_COLLECTION)
            .doc(SERVICE_HARDWARE)
            .get()
            .pipe(map(data => {
                this.hardwareService = { id: data.id, ref: data.ref, ...data.data() };
            }));
    }

    isAllHardwareFullCopV2() {
        return this.hardwareService?.instances?.every((inst: any) => inst.status === STATUS_FULL);
    }

    isAnyPersonPossibleToGetASmartphone() {
        const employee = this.userService.loggedUser;
        const cop = this.consultancyProcessGlobal;

        const employeeData = {
            name: `${employee.firstName} ${employee.lastName}`,
            possible:
                (cop.employeeSmartphoneData &&
                    cop.employeeSmartphoneData.output.employeeCanGetNewMobile) ||
                (cop.employeeSmartphoneData &&
                    cop.employeeSmartphoneData.output.employeeMustGetNewMobile),
        };

        const personMembers =
            (cop.consultancyEmployeeForm &&
                cop.consultancyEmployeeForm.peopleAtSameHouse
                    .filter((person: any) => person.smartphoneData && person.smartphoneData.output)
                    .map((item: any) => {
                        return {
                            name: item.name,
                            possible: item.smartphoneData.output.memberCanGetNewMobile,
                        };
                    })) ||
            [];

        return ([ employeeData, ...personMembers ].filter((item) => item.possible).length > 0);
    }

    isBenefitCalculatorAllChartsArraysEmpty() {
        if (
            this.consultancyProcessGlobal &&
            this.consultancyProcessGlobal.benefitCalculator
        ) {
            const output =
                this.consultancyProcessGlobal.benefitCalculator.output;
            return (
                output &&
                output.chart &&
                output.chart.length === 0 &&
                output.chartExtended &&
                output.chartExtended.length === 0
            );
        } else {
            return false;
        }
    }

    canShowTheSlider() {
        return (!this.isBenefitCalculatorAllChartsArraysEmpty() ||
            (this.isBenefitCalculatorAllChartsArraysEmpty() && this.isEmployeeOlderThanTheBAVLimitAge()) ||
            (this.isBenefitCalculatorAllChartsArraysEmpty() && this.isEmployeeUsingFullPotentialBAV())) && !this.isBavDenied();
    }

    isBenefitCalculatorWithoutChartsArrays() {
        const output = this.consultancyProcessGlobal.benefitCalculator.output;
        return output && !output.chart && !output.chartExtended;
    }

    isAllPeopleFilledSmartphoneData() {
        return this.allPeopleSmartphoneData.every(
            (person) => person.smartphoneData
        );
    }

    isBavDenied() {
        return this.consultancyProcessGlobal?.benefitCalculator?.output?.message === 'BAV_DENIED' ||
            this.isDisableBav;
    }

    hasNoNetAdvantage() {
        return this.consultancyProcessGlobal?.benefitCalculator?.output?.netAdvantage === 0;
    }

    isEmployeeUsingFullPotentialBAV() {
        if (
            this.consultancyProcessGlobal &&
            this.consultancyProcessGlobal.benefitCalculator
        ) {
            return (
                this.consultancyProcessGlobal.benefitCalculator.input
                    .currentBav >=
                MAXIMUM_BAV_DEPOSIT - MINIMUM_BAV_PAYMENT
            );
        }
        return false;
    }

    isEmployeeOlderThanTheBAVLimitAge() {
        if (
            this.consultancyProcessGlobal &&
            this.consultancyProcessGlobal.benefitCalculator
        ) {
            const dateObj = DateConverter.toDate(
                this.consultancyProcessGlobal.benefitCalculator.input.birthday
            );
            return moment().diff(dateObj, 'years') > MAXIMUM_AGE_BAV;
        }
        return false;
    }

    isNetAdvantageIsEqualsZero() {
        if (this.consultancyProcessGlobal) {
            const benefitCalculator =
                this.consultancyProcessGlobal.benefitCalculator || {};
            return (
                benefitCalculator.output &&
                benefitCalculator.output.netAdvantage === 0
            );
        }
    }

    confirmCopWithoutOptimization(): Observable<any> {
        return this.callFunction(
            this.userService.operatedByHelperUser ? {helperMetadata: {operatedByHelperUser: true, employeeId: this.userService.loggedUser.id}} : null,
            CONFIRM_COP_WITHOUT_OPTIMIZATION_FUNCTION,
            new FunctionOptions('callable')
        );
    }

    private getChart() {
        return this.consultancyProcessGlobal &&
        this.consultancyProcessGlobal.benefitCalculator &&
        this.consultancyProcessGlobal.benefitCalculator.output &&
        this.consultancyProcessGlobal.benefitCalculator.output.chart ?
            this.consultancyProcessGlobal.benefitCalculator.output.chart : [];
    }

    private getChartExtended() {
        return this.consultancyProcessGlobal &&
        this.consultancyProcessGlobal.benefitCalculator &&
        this.consultancyProcessGlobal.benefitCalculator.output &&
        this.consultancyProcessGlobal.benefitCalculator.output.chartExtended ?
            this.consultancyProcessGlobal.benefitCalculator.output.chartExtended : [];
    }

    addSalaryIncreaseService() {
        if (this.consultancyProcessGlobal?.isSalaryIncrease &&
            this.consultancyProcessGlobal?.benefitCalculator?.output?.notUsedIncreaseNet > 0) {
            this.benefitsServices?.push({
                id: SERVICE_SALARY_INCREASE_SUMMARY,
                additionalInfo: 'cop.net-benefit.summary-services-selection.additional-info-service-salary-increase-more-net',
                status: STATUS_ACTIVE,
                value: this.consultancyProcessGlobal.benefitCalculator.output.notUsedSalaryIncrease,
                moreNet: this.consultancyProcessGlobal.benefitCalculator.output.notUsedIncreaseNet,
                advantage: this.consultancyProcessGlobal.benefitCalculator.output.notUsedSalaryIncreaseNet,
                generalBenefitService: {
                    nameEn: 'Normal gross salary increase',
                    nameDe: 'Normale Bruttogehaltserhöhung',
                    icon: 'assets%2Ficons%2Fservice-salary-increase-more-net.svg?alt=media&token=244239a2-0d6e-48a0-a290-d8061ec10f25',
                }
            });
        }
    }
    get isDisableBav() {
        return !this.isCompanyRetirementServiceActive || this.consultancyProcessGlobal?.disableBav;
    }

    private setRetirementService(
        globalRetirementRef: AngularFirestoreDocument<any>,
        retirementServiceRef: AngularFirestoreDocument<any>,
        depositBAV: number,
        previousValues?: any
    ) {
        return globalRetirementRef.get().pipe(
            take(1),
            map((res) => {
                const data: any = res.data();
                data.id = res.id;
                return data;
            }),
            concatMap((res) => {
                return retirementServiceRef.set(
                    {
                        status: STATUS_ACTIVE,
                        previousValues: previousValues ? previousValues : null,
                        indexOrder: res?.indexOrder,
                        service: globalRetirementRef.ref,
                        value: depositBAV,
                        type: this.consultancyProcessGlobal
                            ?.netBenefitsDistribution?.type,
                        lastModifiedFrom: 'WEBSITE (updateRetirementService)',
                    },
                    {
                        merge: true,
                    }
                );
            }),
            catchError((error) => {
                console.log(error);
                const path = `${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/
                ${COLLECTION_CONSULTANCY_PROCESS}/${this.consultancyProcessGlobal.id}/${SERVICE_RETIREMENT}`;
                const message = error.error ? error.error.message : error.message;
                this.fireLogging.sendErrorLog(`[setRetirementService
                (cop-home.service.ts)] An error occurred while updating item into collection ${path},
                 details: ${message || JSON.stringify(error)}`);
                return of(null);
            })
        );
    }

    private rollbackRetirementService(
        globalRetirementRef: AngularFirestoreDocument<any>,
        retirementServiceRef: AngularFirestoreDocument<any>,
        previousValues: any
    ) {
        return globalRetirementRef.get().pipe(
            take(1),
            map((res) => {
                const data: any = res.data();
                data.id = res.id;
                return data;
            }),
            concatMap((res) => {
                return retirementServiceRef.set(
                    {
                        status: STATUS_FULL,
                        previousValues: previousValues,
                        indexOrder: res?.indexOrder || 99,
                        service: globalRetirementRef.ref,
                        value: previousValues.depositBAV,
                        type: previousValues?.type ? previousValues.type : deleteField(),
                        lastModifiedFrom: 'WEBSITE (rollbackRetirementService)',
                    },
                    {
                        merge: true,
                    }
                );
            }),
            catchError((error) => {
                console.log(error);
                const path = `${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/
                ${COLLECTION_CONSULTANCY_PROCESS}/${this.consultancyProcessGlobal.id}/${SERVICE_RETIREMENT}`;
                const message = error.error ? error.error.message : error.message;
                this.fireLogging.sendErrorLog(`[rollbackRetirementService
                (cop-home.service.ts)] An error occurred while updating item into collection ${path},
                 details: ${message || JSON.stringify(error)}`);
                return of(null);
            })
        );
    }
}
